import {
  FirstDayOfWeek,
  MonthType,
  OnDatesChangeProps,
  useDatepicker,
} from "@datepicker-react/hooks";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useState, type JSX } from "react";
import { getLang } from "@hopper-b2b/i18n";
import { ActionLink } from "../../../../../ActionLink";

import { Month } from "../Month";
import "./styles.scss";
import { firstWeekDay } from "../../component";

export interface IHorizontalViewProps {
  startDate: Date | null;
  cleanedEndDate: Date | null;
  setStartDate?: (date: Date | null) => void;
  setEndDate?: (date: Date | null) => void;
  minAllowedDate: Date | null;
  maxAllowedDate: Date | null;
  numberOfMonths: number;
  showPriceRangeTags?: boolean;
  priceTags?: string[];
  priceRangeTags: JSX.Element;
  className?: string;
  unavailableDates?: Date[];
}

export const HorizontalView = ({
  startDate,
  cleanedEndDate,
  setStartDate,
  setEndDate,
  minAllowedDate,
  maxAllowedDate,
  numberOfMonths,
  showPriceRangeTags,
  priceRangeTags,
  priceTags,
  className,
  unavailableDates,
}: IHorizontalViewProps) => {
  const handleDateChange = (props: OnDatesChangeProps) => {
    const { startDate, endDate } = props;
    setStartDate && setStartDate(startDate);
    setEndDate && setEndDate(endDate);
  };

  const {
    firstDayOfWeek,
    activeMonths,
    goToPreviousMonthsByOneMonth,
    goToNextMonthsByOneMonth,
  } = useDatepicker({
    firstDayOfWeek: firstWeekDay(getLang()) as FirstDayOfWeek, // 0 is Sunday.
    startDate,
    endDate: cleanedEndDate,
    focusedInput: null,
    onDatesChange: handleDateChange,
    numberOfMonths,
    unavailableDates,
  });

  const [firstMonth, secondMonth] = activeMonths;

  const [currentFocusDate, setCurrentFocusDate] = useState<Date | undefined>(
    dayjs().toDate()
  );

  const [isDateFocused, setIsDateFocused] = useState<boolean>(true);

  const leftArrowDisabled = minAllowedDate
    ? isLessThanMonth(firstMonth, minAllowedDate)
    : false;

  const rightArrowDisabled = maxAllowedDate
    ? isGreaterThanMonth(secondMonth, maxAllowedDate)
    : false;

  const getYearMonthFloat = (date: Date | undefined) =>
    parseFloat(`${dayjs(date).format("YYYYMM")}`);

  useEffect(() => {
    const currentFocusYearMonth = getYearMonthFloat(currentFocusDate);
    const secondMonthYearMonth = getYearMonthFloat(secondMonth.date);
    const firstMonthYearMonth = getYearMonthFloat(firstMonth.date);
    if (currentFocusYearMonth > secondMonthYearMonth && !rightArrowDisabled)
      goToNextMonthsByOneMonth();

    if (currentFocusYearMonth < firstMonthYearMonth && !leftArrowDisabled)
      goToPreviousMonthsByOneMonth();

    if (dayjs(currentFocusDate).isBefore(dayjs(), "date"))
      // eslint-disable-next-line react-hooks/set-state-in-effect -- This effect syncs currentFocusDate with calendar constraints
      setCurrentFocusDate(dayjs().toDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This effect intentionally only runs when currentFocusDate changes to sync with calendar navigation
  }, [currentFocusDate]);

  useEffect(() => {
    if (dayjs(currentFocusDate).isBefore(dayjs(firstMonth.date), "month"))
      // eslint-disable-next-line react-hooks/set-state-in-effect -- This effect syncs currentFocusDate when calendar months change
      setCurrentFocusDate(
        dayjs(firstMonth.date).date(dayjs(currentFocusDate).date()).toDate()
      );
    if (dayjs(currentFocusDate).isAfter(dayjs(secondMonth.date), "month"))
      // eslint-disable-next-line react-hooks/set-state-in-effect -- This effect syncs currentFocusDate when calendar months change
      setCurrentFocusDate(
        dayjs(secondMonth.date).date(dayjs(currentFocusDate).date()).toDate()
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This effect intentionally only runs when displayed months change to keep currentFocusDate in sync
  }, [firstMonth, secondMonth]);

  const renderMonths = () =>
    [firstMonth, secondMonth].map((month: MonthType) => {
      return (
        <Month
          key={`${month.year}-${month.month}`}
          year={month.year}
          month={month.month}
          firstDayOfWeek={firstDayOfWeek}
          date={month.date}
          className={className}
          currentFocusDate={currentFocusDate}
          setCurrentFocusDate={setCurrentFocusDate}
          setIsDateFocused={setIsDateFocused}
          isDateFocused={isDateFocused}
          priceTags={priceTags}
        />
      );
    });

  return (
    <Box className={clsx("horizontal-view-date-range-picker", className)}>
      <Box className="date-range-picker-section">
        <Box className={clsx("month-shift-button-container", "left")}>
          <ActionLink
            className="month-shift-button"
            label="Go to previous months button"
            onClick={goToPreviousMonthsByOneMonth}
            content={
              <FontAwesomeIcon
                icon={faArrowLeft as IconProp}
                className="month-shift-icon"
              />
            }
            showTappableArea={true}
            disabled={leftArrowDisabled}
          />
        </Box>
        {renderMonths()}
        <Box className={clsx("month-shift-button-container", "right")}>
          <ActionLink
            className="month-shift-button"
            label="Go to next months button"
            onClick={goToNextMonthsByOneMonth}
            content={
              <FontAwesomeIcon
                icon={faArrowRight as IconProp}
                className="month-shift-icon"
              />
            }
            showTappableArea={true}
            disabled={rightArrowDisabled}
          />
        </Box>
      </Box>
      {showPriceRangeTags && !!priceRangeTags && (
        <Box className="price-range-tags-section">{priceRangeTags}</Box>
      )}
    </Box>
  );
};

const isLessThanMonth = (left: MonthType, right: Date) =>
  left.year < right.getFullYear() ||
  (left.year === right.getFullYear() && left.month <= right.getMonth());

const isGreaterThanMonth = (left: MonthType, right: Date) =>
  left.year > right.getFullYear() ||
  (left.year === right.getFullYear() && left.month >= right.getMonth());
